import {
  Box,
  HStack,
  IconButton,
  Text,
  VStack,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import React from 'react';
import { Link } from 'react-router-dom';

import {
  date as commonDate,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { Bank } from '@finley-technologies/finley-types';

import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';
import { formatCurrencyAmount } from '~/packages/common-frontend/utils';
import { useFundingRequestTemplateContextV1 } from '~/packages/funding-requests/hooks/useFundingRequestTemplateContextV1';

type PendingFundingRequestProps = {
  type: 'drawRequest' | 'collectionsRecycle';
};

const PendingFundingRequestIcon = ({ type }: PendingFundingRequestProps) => {
  switch (type) {
    case 'drawRequest':
      return <Icon icon="dollar-sign" />;
    case 'collectionsRecycle':
      return <Icon icon="recycle" />;
    default:
      return finleyAssertUnreachable(type);
  }
};

export const PendingFundingRequests = ({
  type,
}: PendingFundingRequestProps) => {
  const { context } = useFundingRequestTemplateContextV1();

  if (context.allowedActions === 'readOnly') {
    return null;
  }

  const filteredRequests = context.summary.pendingRequests.filter(
    (request) => request.type === type,
  );

  if (filteredRequests.length === 0) {
    return (
      <Alert status="info">
        <AlertIcon />
        <AlertDescription>
          {`No pending ${
            type === 'collectionsRecycle' ? 'recycle' : 'draw'
          } requests`}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <VStack sx={{ gap: '3em' }}>
      {filteredRequests.map(
        ({
          fundingRequestComponentId,
          fundingRequestId,
          amount,
          targetFundingDate,
        }) => (
          <HStack
            key={fundingRequestComponentId}
            sx={{
              w: '100%',
              p: '2em',
              border: `1px solid ${colors.gray[200]}`,
              borderRadius: '1em',
              gap: '3em',
            }}
          >
            <Box
              sx={{
                bgColor: 'gray.50',
                p: '8px',
                // m: '8px',
                color: 'black.95',
                fontSize: '1.5em',
                borderRadius: '.25em',
              }}
            >
              <PendingFundingRequestIcon type={type} />
            </Box>
            <VStack
              sx={{
                alignItems: 'flex-start',
                gap: '1em',
                width: '5em',
              }}
            >
              <Text
                sx={{
                  color: 'black.70',
                }}
              >
                ID
              </Text>
              <Text noOfLines={1} maxWidth="5em">
                {fundingRequestComponentId}
              </Text>
            </VStack>
            <VStack
              sx={{
                alignItems: 'flex-start',
                gap: '1em',
                width: '10em',
              }}
            >
              <Text
                sx={{
                  color: 'black.70',
                }}
              >
                Amount
              </Text>
              <Text>{formatCurrencyAmount(amount, Bank.Currency.USD)}</Text>
            </VStack>
            <VStack
              sx={{
                alignItems: 'flex-start',
                gap: '1em',
              }}
            >
              <Text
                sx={{
                  color: 'black.70',
                }}
              >
                Target Funding Date
              </Text>
              <Text>
                {targetFundingDate
                  ? commonDate.fullDateStrToHumanDateString(targetFundingDate)
                  : '-'}
              </Text>
            </VStack>
            <VStack
              sx={{
                flexGrow: 1,
                alignItems: 'flex-end',
              }}
            >
              <IconButton
                as={Link}
                aria-label="View funding request"
                to={`/funding-requests/${fundingRequestId}`}
                icon={<Icon icon="chevron-right" />}
                variant="ghost"
                colorScheme="v2-aurora"
              />
            </VStack>
          </HStack>
        ),
      )}
    </VStack>
  );
};
